/**
 * Senders picker section — minimal, space-efficient, intuitive.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { BookmarkPlus, ChevronDown, Plus, Search, Trash2, User } from 'lucide-react';

import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import {
  CreativeAddRow,
  SenderPickerItem,
} from '../CreativePickerPrimitives';
import type {
  SendersPickerSectionProps,
  SenderTemplateOption,
} from '../drop-modals.types';

export const SendersPickerSection: FC<SendersPickerSectionProps> = ({
  sendersList,
  selectedSenders,
  onToggleSender,
  onSelectAll,
  onClearSelection,
  onDeleteSender,
  onDeleteSenders,
  search,
  onSearch,
  newValue,
  setNewValue,
  onAdd,
  isBusy,
  expanded,
  onToggleExpand,
  templates = [],
  onSelectTemplate,
  activeTemplateId = null,
  onCreateTemplate,
  canSaveTemplate = false,
  pendingHideSenders,
  replacedByAiSenders,
  actionSlot,
  toolbarSlot,
}) => {
  const [showAddRow, setShowAddRow] = useState(false);

  const handleAdd = useCallback(async () => {
    await onAdd?.();
    if (sendersList.length > 0) setShowAddRow(false);
  }, [onAdd, sendersList.length]);

  const handleTemplateClick = useCallback(
    (t: SenderTemplateOption) => {
      if (!onSelectTemplate) return;
      if (activeTemplateId === t.id) {
        onSelectTemplate(null);
        return;
      }
      onSelectTemplate(t);
    },
    [onSelectTemplate, activeTemplateId],
  );

  const filtered = sendersList
    .filter((s) => s.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const aSel = selectedSenders.includes(a);
      const bSel = selectedSenders.includes(b);
      if (aSel === bSel) return 0;
      return aSel ? -1 : 1;
    });

  const hasSelection = selectedSenders.length > 0;
  const canDelete = hasSelection && (onClearSelection || onDeleteSender || onDeleteSenders);

  return (
    <section className="flex min-h-0 min-w-0 flex-1 flex-col xl:min-w-0">
      <button
        type="button"
        onClick={onToggleExpand}
        className="flex shrink-0 items-center justify-between gap-2 rounded-lg border border-border bg-muted/20 px-3 py-2 text-left transition-colors hover:bg-muted/40"
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <User className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">Senders</span>
          <span
            className={cn(
              'shrink-0 rounded-full px-2 py-0.5 text-xs font-medium',
              hasSelection
                ? 'bg-primary/15 text-primary'
                : 'bg-muted text-muted-foreground',
            )}
          >
            {selectedSenders.length}
          </span>
        </div>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
            expanded && 'rotate-180',
          )}
        />
      </button>

      {expanded && (
        <div
          className={cn(
            'mt-2 flex min-w-0 flex-1 flex-col rounded-lg border border-border bg-card',
            toolbarSlot
              ? 'min-h-[180px] overflow-y-auto'
              : 'min-h-[200px] overflow-hidden sm:min-h-[240px]',
          )}
        >
          {toolbarSlot && (
            <div className="flex min-h-0 max-h-[min(320px,45vh)] min-w-0 shrink-0 flex-col overflow-hidden">
              {toolbarSlot}
            </div>
          )}
          {/* Toolbar: search + actions */}
          <div className={cn('flex shrink-0 flex-col gap-2 border-b border-border bg-muted/5 p-2', toolbarSlot && 'border-t-0')}>
            <div className="flex items-center gap-2">
              <div className="relative min-w-0 flex-1">
                <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  className="h-8 w-full min-w-0 rounded-md border border-input bg-background pl-9 pr-2 text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                  placeholder="Search senders..."
                  value={search}
                  onChange={(e) => onSearch(e.target.value)}
                />
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {actionSlot}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-xs"
                  onClick={onSelectAll}
                  disabled={isBusy}
                >
                  {selectedSenders.length === filtered.length
                    ? 'None'
                    : 'All'}
                </Button>
                {onAdd && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 gap-1 px-2 text-xs"
                    onClick={() => setShowAddRow((v) => !v)}
                    aria-expanded={showAddRow}
                  >
                    <Plus className="h-3.5 w-3.5" /> Add
                  </Button>
                )}
                {canDelete && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
                    disabled={isBusy}
                    onClick={async () => {
                      if (selectedSenders.length > 0) {
                        if (onDeleteSenders) await onDeleteSenders(selectedSenders);
                        else if (onDeleteSender) await Promise.all(selectedSenders.map((name) => onDeleteSender(name)));
                      }
                      onClearSelection?.();
                    }}
                    aria-label={
                      onDeleteSender || onDeleteSenders
                        ? `Remove ${selectedSenders.length} selected`
                        : `Clear ${selectedSenders.length} selected`
                    }
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>

            {/* Templates row — compact chips + Save as template */}
            {(templates.length > 0 || (canSaveTemplate && onCreateTemplate)) && (
              <div className="flex flex-wrap items-center gap-1.5">
                {templates.length > 0 && onSelectTemplate &&
                  templates.map((t) => {
                    const isActive = activeTemplateId === t.id;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => handleTemplateClick(t)}
                        disabled={isBusy}
                        className={cn(
                          'rounded-md px-2 py-1 text-xs font-medium transition-colors',
                          isActive
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted/80 text-muted-foreground hover:bg-muted hover:text-foreground',
                        )}
                        title={
                          isActive
                            ? 'Click again to stop editing template'
                            : `Apply: ${t.senderNames.join(', ')}`
                        }
                      >
                        {t.name}
                      </button>
                    );
                  })}
                {canSaveTemplate && onCreateTemplate && hasSelection && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 gap-1 text-xs"
                    onClick={onCreateTemplate}
                    disabled={isBusy}
                    title="Save current senders as a reusable template"
                  >
                    <BookmarkPlus className="h-3 w-3" /> Save as template
                  </Button>
                )}
              </div>
            )}

            {/* Add sender row */}
            {onAdd && (showAddRow || sendersList.length === 0) && (
              <div className="rounded-md border border-dashed border-border/60 bg-background/50 p-2">
                <CreativeAddRow
                  value={newValue}
                  onChange={setNewValue}
                  placeholder="Paste sender names or JSON"
                  onAdd={handleAdd}
                  disabled={!newValue.trim() || isBusy}
                  inputDisabled={isBusy}
                  multiline
                  onKeyDown={(e) =>
                    e.key === 'Enter' &&
                    !e.shiftKey &&
                    (e.preventDefault(), handleAdd())
                  }
                />
              </div>
            )}
          </div>

          {/* Sender list — scrollable when many senders */}
          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-2 scrollbar-thin">
            {filtered.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                {search ? 'No senders match your search' : 'No senders yet'}
              </p>
            ) : (
              <div className="space-y-0.5">
                {filtered.map((name) => (
                  <SenderPickerItem
                    key={name}
                    isSelected={selectedSenders.includes(name)}
                    onToggle={() => onToggleSender(name)}
                    label={name}
                    pendingHide={pendingHideSenders?.includes(name)}
                    replacedByAi={replacedByAiSenders?.includes(name)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};